import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FaPlus, FaSyncAlt, FaTrash } from "react-icons/fa";
import { useAuth } from "../Login/AuthContext";
import { getRateLimitMessage } from "../../utils/rateLimitFeedback";

const ALERT_COLOR = "#92400e";

const ListaCategorias = () => {
  const [categorias, setCategorias] = useState([]);
  const [nombre, setNombre] = useState("");
  const [filtro, setFiltro] = useState("");
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  const API_URL = process.env.REACT_APP_API_URL;
  const { token } = useAuth();

  const categoriasFiltradas = useMemo(() => {
    const texto = filtro.trim().toLowerCase();
    if (!texto) return categorias;
    return categorias.filter((categoria) =>
      (categoria.nombre || "").toLowerCase().includes(texto)
    );
  }, [categorias, filtro]);

  const cargarCategorias = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await axios.get(`${API_URL}/categorias`);
      setCategorias(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError("No se pudieron cargar las categorias.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarCategorias();
  }, [API_URL]);

  const mostrarError = (err, textoFallback) => {
    const status = err.response?.status;
    const msg =
      err.response?.data?.message ||
      err.response?.data?.msg ||
      err.response?.data?.error ||
      "";
    const titulosPorStatus = {
      400: "No se puede eliminar",
      404: "Categoria no encontrada",
      429: "Demasiadas solicitudes",
    };

    Swal.fire({
      icon: "error",
      title: titulosPorStatus[status] || "Error",
      text: status === 429 ? getRateLimitMessage(err) : msg || textoFallback,
      confirmButtonColor: ALERT_COLOR,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const nombreLimpio = nombre.trim();
    if (!nombreLimpio) {
      Swal.fire({
        icon: "warning",
        title: "Nombre requerido",
        text: "Ingresa un nombre para la categoria.",
        confirmButtonColor: ALERT_COLOR,
      });
      return;
    }

    const yaExiste = categorias.some(
      (categoria) => categoria.nombre?.toLowerCase() === nombreLimpio.toLowerCase()
    );
    if (yaExiste) {
      Swal.fire({
        icon: "warning",
        title: "Categoria repetida",
        text: "Ya existe una categoria con ese nombre.",
        confirmButtonColor: ALERT_COLOR,
      });
      return;
    }

    try {
      setGuardando(true);
      const response = await axios.post(
        `${API_URL}/categorias`,
        { nombre: nombreLimpio },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const nuevaCategoria = response.data || { nombre: nombreLimpio };
      setCategorias((prev) => [...prev, nuevaCategoria]);
      setNombre("");

      Swal.fire({
        icon: "success",
        title: "Categoria agregada",
        text: "La categoria se agrego correctamente.",
        confirmButtonColor: ALERT_COLOR,
      });
    } catch (err) {
      mostrarError(err, "No se pudo agregar la categoria.");
    } finally {
      setGuardando(false);
    }
  };

  const eliminarCategoria = async (categoria) => {
    if (!categoria.id) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se puede eliminar una categoria sin ID.",
        confirmButtonColor: ALERT_COLOR,
      });
      return;
    }

    const result = await Swal.fire({
      title: "Estas seguro?",
      text: `Se eliminara la categoria "${categoria.nombre}".`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: ALERT_COLOR,
      cancelButtonColor: "#64748b",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await axios.delete(`${API_URL}/categorias/${categoria.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setCategorias((prev) => prev.filter((item) => item.id !== categoria.id));

      Swal.fire({
        icon: "success",
        title: "Categoria eliminada",
        text: response.data?.message || "Categoria eliminada correctamente",
        confirmButtonColor: ALERT_COLOR,
      });
    } catch (err) {
      mostrarError(err, "No se pudo eliminar la categoria.");
    }
  };

  return (
    <section className="min-h-screen bg-stone-50 p-4 md:p-8">
      <div className="mx-auto w-full max-w-5xl rounded-lg border border-stone-200 bg-white p-4 shadow-lg md:p-6">
        <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="text-sm font-semibold uppercase tracking-wide text-amber-700">
              Administracion de catalogo
            </span>
            <h2 className="mt-2 text-2xl font-extrabold text-stone-900 md:text-3xl">
              Categorias
            </h2>
          </div>

          <button
            type="button"
            onClick={cargarCategorias}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-stone-300 px-4 py-3 text-sm font-semibold text-stone-800 transition hover:bg-stone-100"
          >
            <FaSyncAlt />
            Actualizar
          </button>
        </div>

        <form onSubmit={handleSubmit} className="mb-6 grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]">
          <div className="flex flex-col">
            <label className="mb-2 text-sm font-medium text-stone-800">
              Nueva categoria
            </label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={80}
              className="w-full rounded-md border border-stone-300 bg-white p-3 focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
          <button
            type="submit"
            disabled={guardando}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-stone-900 px-5 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-amber-800 disabled:opacity-60 md:self-end"
          >
            <FaPlus />
            {guardando ? "Agregando..." : "Agregar"}
          </button>
        </form>

        <div className="mb-4">
          <input
            type="text"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Filtrar por nombre"
            className="w-full rounded-md border border-stone-300 p-2 focus:border-amber-600 focus:outline-none focus:ring-2 focus:ring-amber-500 md:max-w-sm"
          />
        </div>

        <div className="overflow-x-auto rounded-lg border border-stone-200">
          <table className="w-full min-w-[520px] border-collapse">
            <thead>
              <tr className="bg-stone-100 text-stone-900">
                <th className="border border-stone-200 px-4 py-2 text-left">ID</th>
                <th className="border border-stone-200 px-4 py-2 text-left">Nombre</th>
                <th className="border border-stone-200 px-4 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <FilaEstado texto="Cargando categorias..." />
              ) : error ? (
                <FilaEstado texto={error} />
              ) : categoriasFiltradas.length === 0 ? (
                <FilaEstado texto="No hay categorias para mostrar." />
              ) : (
                categoriasFiltradas.map((categoria, index) => (
                  <tr
                    key={categoria.id || categoria.nombre}
                    className={`${index % 2 === 0 ? "bg-white" : "bg-stone-50"} hover:bg-amber-50`}
                  >
                    <td className="border border-stone-200 px-4 py-2 text-stone-700">
                      {categoria.id || "-"}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 font-medium text-stone-900">
                      {categoria.nombre}
                    </td>
                    <td className="border border-stone-200 px-4 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => eliminarCategoria(categoria)}
                        className="inline-flex items-center justify-center text-stone-900 transition hover:text-amber-800"
                        title="Eliminar"
                        aria-label={`Eliminar ${categoria.nombre}`}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

const FilaEstado = ({ texto }) => (
  <tr>
    <td className="px-4 py-8 text-center text-stone-500" colSpan="3">
      {texto}
    </td>
  </tr>
);

export default ListaCategorias;
